import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { useFeatureAllowed } from "@/hooks/use-plan";
import { formatWhatsApp, temWhatsApp, waPhone } from "@/lib/utils";

// Robô de WhatsApp da loja (Ultra). O servidor é a autoridade; daqui a tela só
// decide o que mostrar — e se o painel abre o "Enviar pelo WhatsApp".

export type BotState = "disconnected" | "connecting" | "connected" | "hibernated";

export interface BotConnection {
  state: BotState | null;
  qrcode: string | null;
  phone: string | null;
  profileName: string | null;
}

export interface BotSettings {
  greetingEnabled: boolean;
  greetingText: string | null;
  awayEnabled: boolean;
  awayText: string | null;
  statusEnabled: boolean;
  pauseMinutes: number;
  testNumbers: string[];
  // Conversa com IA e números que o robô não responde (14/10/2026). Opcionais: o Worker sai antes da API.
  aiEnabled?: boolean;
  aiInfo?: string | null;
  ignoredNumbers?: string[];
  // Números que o robô nunca responde, fixos pela plataforma (o suporte do Venda no Zap; Pedro, 16/10/2026): só
  // leitura, fora do `ignoredNumbers` que a loja edita (a API tira de lá e descarta no PATCH). `numero` com DDI e o 9.
  numerosFixosIgnorados?: Array<{ numero: string; motivo: string }>;
  // Só leitura: a última saudação/ausência automática do WhatsApp Business da loja (a do app) vista numa conversa do robô.
  whatsappBusinessAutomaticaEm?: string | null;
}

export interface WhatsappBotState {
  allowed: boolean;
  enabled: boolean;
  connection: BotConnection | null;
  settings: BotSettings | null;
  defaults: { greeting: string; away: string };
}

export const WHATSAPP_BOT_QK = ["whatsapp-bot"] as const;

/** Mesma chave do servidor (lib/whatsappBot phoneKey): celular BR sem o 9. */
export function phoneKey(raw: string | null | undefined): string {
  const d = waPhone(String(raw ?? "").split("@")[0]);
  return d.length === 13 && d.startsWith("55") && d[4] === "9" ? d.slice(0, 4) + d.slice(5) : d;
}

/** Chave sem o 9 → exibição com o 9 de volta quando é celular. */
export function displayPhone(key: string | null | undefined): string {
  let d = String(key ?? "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("55") && /[6-9]/.test(d[4])) d = `${d.slice(0, 4)}9${d.slice(4)}`;
  return formatWhatsApp(d);
}

/** Número das listas do robô (de teste e que ele não responde), guardado como chave. Regra do Pedro (16/10/2026): com
 *  55, parte local de 8 dígitos que não começa com 3 é celular guardado sem o 9 — mostra com o 9 (o displayPhone só
 *  punha o 9 de 6 a 9 e o (11) 9 2104-8695 aparecia sem ele); começando com 3, fixo; 9 dígitos, como veio. Sem 55, o
 *  número como veio. */
export function numeroDaLista(key: string | null | undefined): string {
  const d = String(key ?? "").replace(/\D/g, "");
  if (!d.startsWith("55")) return d;
  return formatWhatsApp(d.length === 12 && d[4] !== "3" ? `${d.slice(0, 4)}9${d.slice(4)}` : d);
}

export function useWhatsappBot() {
  const allowed = useFeatureAllowed("whatsapp_bot");
  return useQuery({
    queryKey: WHATSAPP_BOT_QK,
    queryFn: () => customFetch<WhatsappBotState>("/api/whatsapp-bot"),
    enabled: allowed,
    staleTime: 60_000,
  });
}

/** O robô vai mandar o status sozinho: o painel não abre o "Enviar pelo WhatsApp". */
export function botSendsStatusTo(bot: WhatsappBotState | undefined, phone: string | null | undefined): boolean {
  // Cliente sem WhatsApp de verdade (PDV): o servidor não manda — o painel abre o WhatsApp sem destinatário.
  if (!temWhatsApp(phone)) return false;
  const s = bot?.settings;
  // Sem o direito (whitelist com prazo vencido), o servidor não manda e o interruptor continua ligado:
  // sem este teste, nem o robô nem o painel avisariam o cliente.
  if (!bot?.allowed || !bot.enabled || !s?.statusEnabled || bot.connection?.state !== "connected") return false;
  return s.testNumbers.length === 0 || s.testNumbers.includes(phoneKey(phone));
}
